import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { MaterialLog } from '../../types/technicianJob';
import { Button } from '../ui/Button';

interface MaterialLoggerProps {
  materials: MaterialLog[];
  onAdd: (material: Omit<MaterialLog, 'id' | 'total_cost'>) => void;
  onRemove: (id: string) => void;
  readonly?: boolean;
}

export const MaterialLogger: React.FC<MaterialLoggerProps> = ({ materials, onAdd, onRemove, readonly }) => {
  const [partName, setPartName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState('pcs');
  const [unitCost, setUnitCost] = useState(0);

  const totalCost = materials.reduce((sum, m) => sum + m.total_cost, 0);

  const handleAdd = () => {
    if (!partName.trim()) return;
    onAdd({ part_name: partName.trim(), quantity, unit, unit_cost: unitCost });
    setPartName('');
    setQuantity(1);
    setUnitCost(0);
  };

  return (
    <div className="space-y-3">
      {materials.length > 0 && (
        <div className="space-y-1.5">
          {materials.map(mat => (
            <div key={mat.id} className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-200 font-medium truncate">{mat.part_name}</p>
                <p className="text-[10px] text-slate-500">{mat.quantity} {mat.unit} @ {mat.unit_cost.toFixed(2)}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-sky-400 font-semibold">{mat.total_cost.toFixed(2)}</span>
                {!readonly && (
                  <button onClick={() => onRemove(mat.id)} className="text-slate-500 hover:text-rose-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
          <div className="flex justify-between items-center pt-1 border-t border-slate-800">
            <span className="text-xs text-slate-400">Total Materials</span>
            <span className="text-sm text-sky-400 font-semibold">{totalCost.toFixed(2)}</span>
          </div>
        </div>
      )}

      {!readonly && (
        <div className="bg-slate-900/40 border border-dashed border-slate-700 rounded-lg p-3 space-y-2">
          <input
            type="text"
            placeholder="Part / Material name"
            value={partName}
            onChange={e => setPartName(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 focus:ring-2 focus:ring-sky-500"
          />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 mb-0.5 block">Qty</label>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={e => setQuantity(Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 mb-0.5 block">Unit</label>
              <select
                value={unit}
                onChange={e => setUnit(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:ring-2 focus:ring-sky-500"
              >
                <option value="pcs">pcs</option>
                <option value="kg">kg</option>
                <option value="ltr">ltr</option>
                <option value="mtr">mtr</option>
                <option value="set">set</option>
                <option value="tube">tube</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 mb-0.5 block">Unit Cost</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={unitCost}
                onChange={e => setUnitCost(Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>
          <Button size="sm" variant="outline" icon={<Plus className="w-3.5 h-3.5" />} onClick={handleAdd} disabled={!partName.trim()}>
            Add Material
          </Button>
        </div>
      )}
    </div>
  );
};
